import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip, Typography, Chip } from '@mui/material';
import { EraserButton } from '../EraserButton/EraserButton'

interface FilterFormControlComponentProps {
  isMobile?: boolean; // Optional prop for mobile view
  options: string[];
  selectedOptions: string[];
  inputValue: string;
  setSelectedOptions: (value: string[]) => void;
  setInputValue: (value: string) => void;
  onChange: (value: string[]) => void;
  label: string;
}

const dedupeValues = (values: string[]): string[] => {
  const seen = new Set<string>();
  return values.filter(value => {
    const normalized = String(value).trim().toUpperCase();
    if (!normalized || seen.has(normalized)) {
      return false;
    }
    seen.add(normalized);
    return true;
  });
};

const FilterFormControlComponent: React.FC<FilterFormControlComponentProps> = ({
  isMobile,
  options,
  selectedOptions,
  inputValue,
  setSelectedOptions,
  setInputValue,
  onChange,
  label,
}) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const toggleDropdown = () => {
    setOpen(prev => !prev);
  };

  const handleClose = (event: any, reason: string) => {
    if (reason !== 'selectOption') {
      setOpen(false);
    }
  };

  const getTooltipContent = (label: string) => {
    switch (label) {
      case 'Service Category':
        return 'Service Category\n Determines Whether the Service is in the category of CCDS, Platform Services, IOT Gateway, Datahub';
      case 'Domain':
        return 'Domain\n Determines the domain that can be used to seggregate baed on domains like Equipment, Telemetry, Dealer, Customer, User etc.';
      case 'Service Scope':
        return 'Service Scope\n \u2022 Shared Services - Built with applicable Cat Digital Standards and designed to be consumed by more than 1 application.\n \u2022 App Specific Services - Built and Designed for only one app even and cannot be used by other applications even if they need access to the same dataset.\nEx: Foresight Specific Api\n \u2022 Internal - Designed only for consumption by other services.\n \u2022 Retire - Candidate Api s for Retirements.Ideally no consumers should be using these services.\n \u2022 NonHelios - The Service is not owned by Helios organization.';
      case 'External Consumers':
        return 'External Consumers';
      case 'Service/API':
        return 'Service/API';
      case 'Service Owner':
        return 'Service Owner';
      case 'Product Owner':
        return 'Product Owner';
      case 'Team':
        return 'Team';
      case 'API Ownership':
        return 'API Ownership\n Determines whether the ownership is within the Helios Platform Organization';
      case 'Consumer Name':
        return 'Consumer Name';
      case 'Part of Cat Digital':
        return 'Part of Cat Digital';
      case 'Industry':
        return 'Industry';
      case 'Business Unit':
        return 'Business Unit';
      case 'Enforces Entitlements':
        return 'Is service enforcing entitlements?';
      case 'Entitlements Cat Corp Enabled':
        return 'Indicates whether entitlements cat corp is enabled for the service';
      case 'Customer Merge Functionality':
        return 'Services that respect customer consolidation event.';
      case 'Localization':
        return 'Support for different languages based on the API request.';  
      case 'Service-to-Service Communication':
        return 'Services that support access through internal gateway URL (Not only through APIGEE endpoint).';
      case 'Forced Filter Support':
        return 'Services that force filters based on user details in OAuth token and entitlements header details that are provided in the API request.';
      case 'Internal Access Logging':
        return 'Services that logs requestor details like client ID, request payload, etc in their internal logging like AWS CloudWatch.';
      case 'CAT Support User Action Audit Logging':
        return 'Support for logging user-level actions for support users.';
      case 'Asset Centric':
        return 'Services that provide asset centric details like make, serial number, etc. (in Request or Response).';
      case 'Unauthenticated Access':
        return 'Unauthenticated access is Public APIs that don’t require login, but only deliver safe, non-confidential data.';
        case 'Contract Testing Enabled':
        return 'Indicates whether contract testing is enabled for the service';
      case 'Contract Testing List of Applications':
        return 'List of applications for which contract testing is enabled.';
      case 'Asset Condition Enforcement':
        return 'Asset Condition Enforcement\n Services must apply all asset condition attributes and call the Domain Asset Entitlements API if attribute contains a wildcard like %.';
      case 'Client Type Agnostic Compliance':
        return 'Client Type Agnostic Compliance\n All services must use the "principal" field and the "applicationId" attribute within the entitlements JSON object when storing and retrieving data.\nServices must be designed to operate independently of the client type. Implementations should avoid branching logic based on clientType';
      case 'Common Library Standardization':
        return 'Common Library Standardization\n All services must use the latest stable Entitlements common libraries for auth checks to ensure consistency, security, and maintainability.';
      case 'Dynamic Rule Externalization':
        return 'Dynamic Rule Externalization\n Services should avoid embedding application-specific logic directly within their codebase. Instead, they should operate based on configurable actions and filter conditions defined in the Entitlements framework.';
      case 'Corp Experience Flag Resilience':
        return 'Corp Experience Flag Resilience\n Services must validate that the isCorpExp is set\nwhen either:\nfilterConditions is not provided, or filterConditions.partyNumbers is missing.\nThis validation ensures that the entitlement configuration is not malformed and maintains integrity in the absence of specific filtering criteria.';
      case 'Entitlements Contract Compliance':
        return 'Entitlements Contract Compliance\n Always follow the Entitlements Swagger contract when integrating with the service.';
      case 'Filter Condition Enforcement':
        return 'Filter Condition Enforcement\n Services must apply all entitlement filter conditions, except partyNumbers, which can be skipped if the data is not party-specific.';
      case 'New Attribute Tolerance':
        return 'New Attribute Tolerance\n APIs must handle new or additional attributes in the entitlements object without breaking or failing.';
      case 'Session Cache Key Standard':
        return 'Session Cache Key Standard\n Services integrated with Sessions must cache responses using a key composed of sessionId, domains, and optionally partyNumber.\ncacheKey = sessionId + domains [+ partyNumber] ';
      case 'Standard Internal Access Control':
        return 'Standard Internal Access Control\n Services must use the standard internal auth mechanisms for service calls unless explicitly exempted by the Entitlements team.';
      case 'Token Decoding Restriction':
        return 'Token Decoding Restriction\n Services must not decode tokens unless explicitly approved by the Entitlements team to maintain centralized control and security.';
      default:
        return 'Information';
    }}

  return (  
    <>      
      <FormControl
        fullWidth
        sx={{ mt: 1, mb: 2, width: '100%', height: '100%', marginBottom: '0' }}
      >
        <FormLabel
          sx={{
            display: 'flex',
            alignItems: 'center',
            textTransform: 'uppercase',
            fontWeight: 800,
            color: isDarkMode ? theme.palette.grey[300] : '#1a1a1a',
            justifyContent: 'space-between',
            fontSize: '10.5px',
            letterSpacing: '-0.3px',
          }}
        >

          <div
            style={{
              fontSize: '12px',
              fontWeight: 750,
              letterSpacing: '-0.3px',
              textTransform: 'uppercase',
              color: isDarkMode ? theme.palette.grey[300] : '#1a1a1a',
            }}
          >
            {label}
            <Tooltip title={
                  <Typography style={{ whiteSpace: 'pre-line', fontSize: 14 }}>
                    {getTooltipContent(label)}
                  </Typography>
                }>
              <sup style={{ verticalAlign: 'super', fontSize: '0.7em', marginLeft: '2px' }}>
                <InfoOutlinedIcon fontSize="inherit" />
              </sup>
            </Tooltip>
          </div>

          <Tooltip title="Clear">
            <div>
              <EraserButton
                setSelectedOptions={setSelectedOptions}
                setInputValue={setInputValue}
                onChange={onChange}
                imagePath ="M8.086 2.207a2 2 0 0 1 2.828 0l3.879 3.879a2 2 0 0 1 0 2.828l-5.5 5.5A2 2 0 0 1 7.879 15H5.12a2 2 0 0 1-1.414-.586l-2.5-2.5a2 2 0 0 1 0-2.828zm2.121.707a1 1 0 0 0-1.414 0L4.16 7.547l5.293 5.293 4.633-4.633a1 1 0 0 0 0-1.414zM8.746 13.547 3.453 8.254 1.914 9.793a1 1 0 0 0 0 1.414l2.5 2.5a1 1 0 0 0 .707.293H7.88a1 1 0 0 0 .707-.293z"
              />
            </div>
          </Tooltip>

        </FormLabel>
        <Autocomplete
          multiple
          disablePortal
          open={open}
          onOpen={toggleDropdown}
          onClose={handleClose}
          slotProps={{
            popper: {
              style: { zIndex: 1200 },
              sx: {
                '& .MuiAutocomplete-listbox': {
                  overflowX: 'auto',
                  maxWidth: '100%',
                },
                '& .MuiPaper-root': {
                  overflowX: 'auto',
                }
              }
            }
          }}
          options={dedupeValues(options).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))}
          value={selectedOptions}
          onChange={(event, newValue) => {
            const dedupedValue = dedupeValues(newValue);
            setSelectedOptions(dedupedValue);
            onChange(dedupedValue);
          }}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          popupIcon={<ExpandMoreIcon />}
          renderTags = {(selectedData, getTagProps) => {
            const visibleTags = selectedData.slice(0, 2);
            return [
              ...visibleTags.map((option, index) => (
                <Chip
                  label={option}
                  size='small'
                  {...getTagProps({ index })}
                  key={option}
                  title={option}
                />
              )),
              selectedData.length > 2 && (
                <Chip
                  label={`+${selectedData.length - 2} more`}
                  size='small'
                  key='more'
                />
              )
            ]
          }}
          renderInput={params => (
            <TextField             
              {...params}
              variant="outlined"
              fullWidth={!isMobile} // Use fullWidth only if not mobile
              placeholder={selectedOptions.length === 0 ? 'All' : ''}
              sx={{
                ...(isMobile ? { width: '240px' } : { width: '100%' }),
                '& .MuiInputBase-root': {
                  flexWrap: 'wrap',
                  paddingRight: '55px !important',
                  ...(isMobile ? { width: '240px' } : { width: '100%' }),
                  fontSize: isMobile ? '12px' : '15px',
                },
                '& .MuiChip-root': {
                  margin: '3px',
                },
              }}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...restProps } = props;

            return (
              <li key={key} {...restProps} 
              style={{
                display: 'flex',
                alignItems: 'center', 
                padding: '8px',
                whiteSpace: 'nowrap',
                minWidth: 'max-content'
              }}>
                <Checkbox
                  checked={selectedOptions.includes(option)}
                  sx={{
                    color: 'black',
                    '&.Mui-checked': {
                      color: 'black',
                    },
                  }}
                />

                <div 
                  style={{ 
                    fontSize: isMobile ? '12px' : '13px',
                    whiteSpace: 'nowrap'
                  }}>
                  {option}
                </div>
              </li>
            );
          }}
        />
      </FormControl>
      
    </>
  );
};

export default FilterFormControlComponent;
